// src/schemas/health.ts
import { z } from "zod";
var HealthResponseSchema = z.object({
  ok: z.boolean(),
  version: z.string(),
  message: z.string().optional(),
  // New in Phase 1 (optional for backward-compat with Phase 0 --version --json):
  daemonVersion: z.string().optional(),
  registryCount: z.number().int().nonnegative().optional(),
  paired: z.boolean().optional()
  // The `understand` block reported the knowledge-graph viewer's install state.
  // It went with the viewer itself in `retire-v1-surfaces`: the code-intelligence
  // delta withdraws `Viewer Asset Installation` and says the analysis status is
  // no longer reported anywhere in the dashboard.
});

// src/schemas/auth.ts
import { z as z2 } from "zod";
var TokenSchema = z2.string().regex(/^[0-9a-f]{8}(-[0-9a-f]{8}){7}$/, {
  message: "token must be 8 dash-separated 8-char hex groups (D-13)"
});
var AuthFileSchema = z2.object({
  version: z2.literal(1),
  token: TokenSchema,
  rotatedAt: z2.string().datetime(),
  agentVersion: z2.string().min(1)
});

// src/schemas/registry.ts
import { z as z3 } from "zod";
var RegistryEntrySchema = z3.object({
  id: z3.string().min(1),
  name: z3.string().min(1),
  root: z3.string().min(1),
  client: z3.string().nullable(),
  addedAt: z3.string().datetime(),
  tags: z3.array(z3.string())
});
var RegistryFileSchema = z3.object({
  version: z3.literal(1),
  projects: z3.array(RegistryEntrySchema)
});
var OpenChangeSummarySchema = z3.object({
  name: z3.string().min(1),
  completedTasks: z3.number().int().nonnegative(),
  totalTasks: z3.number().int().nonnegative(),
  /**
   * False when the change has no task artifact at all. Distinct from 0 of 0:
   * the OpenSpec CLI reports zero for absent and empty alike, so presence is a
   * value in its own right rather than something inferred from a count.
   */
  hasTaskArtifact: z3.boolean()
});
var ProjectConditionSchema = z3.enum([
  "unreachable",
  "migrated",
  "needs-migration",
  "no-workflow"
]);
var RegistryListItemSchema = RegistryEntrySchema.extend({
  status: z3.object({
    reachable: z3.boolean(),
    condition: ProjectConditionSchema,
    openChanges: z3.array(OpenChangeSummarySchema),
    capabilityCount: z3.number().int().nonnegative(),
    lastCommitAt: z3.string().datetime().nullable()
  }).strict()
});
var RegistryListResponseSchema = z3.array(RegistryListItemSchema);
var RegisterResponseSchema = RegistryEntrySchema.extend({
  alreadyRegistered: z3.boolean()
});
var StatusResponseSchema = z3.object({
  reachable: z3.boolean(),
  uptime: z3.number().int().nonnegative(),
  bindUrl: z3.string().url(),
  registryCount: z3.number().int().nonnegative(),
  pairedSince: z3.string().datetime().nullable(),
  tokenAge: z3.number().int().nonnegative()
});
var RegisterPrepareRequestSchema = z3.object({
  path: z3.string().min(1)
});
var DetectedMarkersSchema = z3.object({
  gitRepo: z3.boolean(),
  planning: z3.boolean(),
  claudeSkills: z3.boolean()
});
var NonceHexSchema = z3.string().regex(/^[0-9a-f]{32}$/);
var RegisterPrepareAllowedSchema = z3.object({
  canonicalRoot: z3.string(),
  suggestedName: z3.string(),
  suggestedSlug: z3.string(),
  alreadyRegistered: z3.literal(false),
  blocked: z3.literal(false),
  detectedMarkers: DetectedMarkersSchema,
  nonce: NonceHexSchema,
  expiresAt: z3.number().int()
});
var RegisterPrepareBlockedSchema = z3.object({
  canonicalRoot: z3.string(),
  blocked: z3.literal(true),
  blockedReason: z3.string()
});
var RegisterPrepareAlreadyRegisteredSchema = z3.object({
  canonicalRoot: z3.string(),
  alreadyRegistered: z3.literal(true),
  existingEntry: RegistryEntrySchema
});
var RegisterPrepareResponseSchema = z3.union([
  RegisterPrepareAllowedSchema,
  RegisterPrepareBlockedSchema,
  RegisterPrepareAlreadyRegisteredSchema
]);
var RegisterConfirmRequestSchema = z3.object({
  nonce: NonceHexSchema,
  name: z3.string().min(1).optional(),
  client: z3.string().nullable().optional(),
  tags: z3.array(z3.string()).optional()
});
var RegisterConfirmResponseSchema = RegisterResponseSchema;
var RenameRequestSchema = z3.object({
  name: z3.string().min(1).max(200)
});
var TagsRequestSchema = z3.object({
  tags: z3.array(z3.string().max(50)).max(20)
});

// src/schemas/overview.ts
import { z as z4 } from "zod";
var MarkersSchema = z4.object({
  gitRepo: z4.boolean(),
  planning: z4.boolean(),
  claudeSkills: z4.boolean()
});
var ProjectOverviewSchema = z4.object({
  tdd: z4.object({
    greenPairs: z4.number().int().nonnegative(),
    totalTasks: z4.number().int().nonnegative()
  }).nullable(),
  branch: z4.string().nullable(),
  markers: MarkersSchema
}).strict();

// src/schemas/openspec.ts
import { z as z5 } from "zod";
var OpenspecChangeDetailSchema = OpenChangeSummarySchema.strict();
var OpenspecCapabilitySchema = z5.object({
  id: z5.string().min(1),
  requirementCount: z5.number().int().nonnegative()
}).strict();
var OpenspecProjectStateSchema = z5.object({
  present: z5.boolean(),
  openChanges: z5.array(OpenspecChangeDetailSchema),
  capabilities: z5.array(OpenspecCapabilitySchema)
}).strict();

// src/schemas/readiness.ts
import { z as z6 } from "zod";
var CHECK_IDS = [
  "workflow",
  "spec",
  "code-review",
  "security-review",
  "pen-test",
  "coverage"
];
var CheckIdSchema = z6.enum(CHECK_IDS);
var CHECK_STATUSES = [
  "ok",
  "warn",
  "fail",
  "stale",
  "never",
  "na"
];
var CheckStatusSchema = z6.enum(CHECK_STATUSES);
var CheckSourceSchema = z6.enum(["derived", "declared"]);
var MAX_PATH_LENGTH = 512;
var MAX_TEXT_LENGTH = 600;
var MAX_VALUE_LENGTH = 200;
var MAX_PATH_PATTERNS = 100;
var RepoRelativePathSchema = z6.string().min(1).max(MAX_PATH_LENGTH).superRefine((value, ctx) => {
  const reject = (message) => ctx.addIssue({ code: z6.ZodIssueCode.custom, message });
  if (/[\u0000-\u001f\u007f]/.test(value))
    reject("path contains a control character");
  if (value.includes("\\")) reject("path must use forward slashes");
  if (value.startsWith("/")) reject("path must be repo-relative");
  if (value.startsWith("~")) reject("path must not be home-relative");
  if (/^[A-Za-z]:/.test(value)) reject("path must not carry a drive letter");
  for (const segment of value.split("/")) {
    if (segment === "") reject("path must not contain an empty segment");
    if (segment === "." || segment === "..") reject("path must not traverse");
  }
});
var FILESYSTEM_ROOTS = "Users|home|var|tmp|private|etc|opt|usr|mnt|Volumes|root|srv|System|Library|Applications";
var STRONG_BOUNDARY = `(?:^|[\\s"'\`([<])`;
var ABSOLUTE_PATH = new RegExp(
  [
    `${STRONG_BOUNDARY}/`,
    // absolute POSIX path at a strong boundary
    `${STRONG_BOUNDARY}\\\\\\\\`,
    // UNC \\\\server at a strong boundary
    // Colon-adjacent, filesystem-looking, and only where the token holding the
    // colon carries no path separator of its own.
    `${STRONG_BOUNDARY}[^\\s"'\`([</]*:/(?:${FILESYSTEM_ROOTS})\\b`,
    `${STRONG_BOUNDARY}[A-Za-z]:[\\\\/]`
    // drive letter
  ].join("|")
);
function carriesAbsolutePath(text) {
  return ABSOLUTE_PATH.test(text);
}
var SanitisedTextSchema = z6.string().max(MAX_TEXT_LENGTH).refine((value) => !carriesAbsolutePath(value), {
  message: "text must not carry an absolute filesystem path"
});
function wireSafeText(text, fallback) {
  return SanitisedTextSchema.safeParse(text).success ? text : fallback;
}
var Rfc3339Schema = z6.string().datetime({ offset: true });
var CommitShaSchema = z6.string().regex(/^[0-9a-f]{40}$/);
var MAX_DATE_MS = 864e13;
var EpochMsSchema = z6.number().int().nonnegative().max(MAX_DATE_MS);
var EvidenceSchema = z6.object({
  path: RepoRelativePathSchema,
  commit: CommitShaSchema.nullable()
}).strict();
var CheckErrorSchema = z6.object({
  code: z6.string().min(1).max(64).regex(/^[a-z][a-z0-9-]*$/),
  message: SanitisedTextSchema
}).strict();
function checkResultObject(idSchema) {
  return z6.object({
    id: idSchema,
    status: CheckStatusSchema,
    source: CheckSourceSchema,
    at: EpochMsSchema.nullable(),
    value: z6.union([z6.string().max(MAX_VALUE_LENGTH), z6.number().finite()]).nullable(),
    threshold: z6.number().finite().nullable(),
    summary: z6.string().max(MAX_TEXT_LENGTH),
    evidence: EvidenceSchema.nullable(),
    error: CheckErrorSchema.nullable()
  }).strict();
}
function refineResult(value, ctx) {
  if (value.error !== null && value.status !== "fail") {
    ctx.addIssue({
      code: z6.ZodIssueCode.custom,
      path: ["status"],
      message: "an error-bearing result must use status fail"
    });
  }
  if (value.status === "na" && value.summary.trim() === "") {
    ctx.addIssue({
      code: z6.ZodIssueCode.custom,
      path: ["summary"],
      message: "a not-applicable result must state its reason"
    });
  }
  if (value.status === "never" || value.status === "na") {
    if (value.at !== null) {
      ctx.addIssue({
        code: z6.ZodIssueCode.custom,
        path: ["at"],
        message: `a ${value.status} result has no observed time`
      });
    }
    if (value.evidence !== null) {
      ctx.addIssue({
        code: z6.ZodIssueCode.custom,
        path: ["evidence"],
        message: `a ${value.status} result has no evidence`
      });
    }
  }
}
var CheckResultSchema = checkResultObject(CheckIdSchema).superRefine(
  refineResult
);
var RemedySchema = z6.string().min(1).max(MAX_TEXT_LENGTH);
var summaryCheck = (id) => checkResultObject(z6.literal(id)).superRefine(refineResult);
var detailCheck = (id) => checkResultObject(z6.literal(id)).extend({ remedy: RemedySchema }).superRefine(refineResult);
var SummaryChecksSchema = z6.tuple([
  summaryCheck(CHECK_IDS[0]),
  summaryCheck(CHECK_IDS[1]),
  summaryCheck(CHECK_IDS[2]),
  summaryCheck(CHECK_IDS[3]),
  summaryCheck(CHECK_IDS[4]),
  summaryCheck(CHECK_IDS[5])
]);
var DetailChecksSchema = z6.tuple([
  detailCheck(CHECK_IDS[0]),
  detailCheck(CHECK_IDS[1]),
  detailCheck(CHECK_IDS[2]),
  detailCheck(CHECK_IDS[3]),
  detailCheck(CHECK_IDS[4]),
  detailCheck(CHECK_IDS[5])
]);
var ADVISORY_WHEN_UNDECLARED = ["pen-test"];
function isAdvisoryCheck(id) {
  return ADVISORY_WHEN_UNDECLARED.includes(id);
}
function computeReady(checks, notice) {
  const exempt = (check) => check.status === "never" && check.source === "derived" && isAdvisoryCheck(check.id) && notice === null;
  const blocked = checks.some(
    (check) => check.error !== null || check.status === "fail" || check.status === "stale" || check.status === "never" && !exempt(check)
  );
  if (blocked) return false;
  return checks.some((check) => check.status === "ok" || check.status === "warn");
}
var ReadinessNoticeSchema = z6.object({
  code: z6.enum([
    "readiness-file-unsupported-version",
    "readiness-file-unparsable",
    "readiness-file-invalid"
  ]),
  message: SanitisedTextSchema
}).strict();
var RepoFamilySchema = z6.enum([
  "agenticapps",
  "factiv",
  "neuroflash",
  "other"
]);
function repoObject(checksSchema) {
  return z6.object({
    id: z6.string().min(1),
    name: z6.string().min(1),
    family: RepoFamilySchema,
    ready: z6.boolean(),
    lastCommitAt: EpochMsSchema.nullable(),
    checks: checksSchema,
    notice: ReadinessNoticeSchema.nullable()
  }).strict();
}
function refineReady(value, ctx) {
  if (value.ready !== computeReady(value.checks, value.notice)) {
    ctx.addIssue({
      code: z6.ZodIssueCode.custom,
      path: ["ready"],
      message: "ready must follow from the six results"
    });
  }
}
var RepoSummarySchema = repoObject(SummaryChecksSchema).superRefine(
  refineReady
);
var RepoDetailSchema = repoObject(DetailChecksSchema).superRefine(
  refineReady
);
var FleetResponseSchema = z6.object({
  generatedAt: EpochMsSchema,
  repos: z6.array(RepoSummarySchema)
}).strict();
var RepoDetailResponseSchema = z6.object({
  generatedAt: EpochMsSchema,
  repo: RepoDetailSchema
}).strict();
var declaredBase = {
  observedAt: Rfc3339Schema,
  summary: z6.string().max(MAX_TEXT_LENGTH).optional(),
  value: z6.union([z6.string().max(MAX_VALUE_LENGTH), z6.number().finite()]).optional()
};
var declaredEvidence = {
  evidence: RepoRelativePathSchema,
  commit: CommitShaSchema
};
function plainDeclaration(id) {
  return z6.object({ id: z6.literal(id), status: CheckStatusSchema, ...declaredBase }).strict();
}
function reviewDeclaration(id) {
  return z6.object({
    id: z6.literal(id),
    status: CheckStatusSchema,
    ...declaredBase,
    ...declaredEvidence
  }).strict();
}
var SubstantiatedPenTestSchema = z6.object({
  id: z6.literal("pen-test"),
  status: z6.enum(["ok", "warn", "fail"]),
  ...declaredBase,
  ...declaredEvidence,
  validUntil: Rfc3339Schema
}).strict();
var UnsubstantiatedPenTestSchema = z6.object({
  id: z6.literal("pen-test"),
  status: z6.enum(["never", "na"]),
  // `declaredBase` minus `observedAt`: there is nothing to have observed. The
  // `na`-states-a-reason rule reads `summary` and is shared with every other
  // check, so it is not restated here.
  summary: declaredBase.summary,
  value: declaredBase.value
}).strict();
var DeclarationSchema = z6.union([
  plainDeclaration("workflow"),
  plainDeclaration("spec"),
  reviewDeclaration("code-review"),
  reviewDeclaration("security-review"),
  SubstantiatedPenTestSchema,
  UnsubstantiatedPenTestSchema,
  plainDeclaration("coverage")
]);
var KNOWN_CHECK_IDS = new Set(CHECK_IDS);
var DeclarationsSchema = z6.preprocess(
  (raw) => Array.isArray(raw) ? raw.filter(
    (entry) => !(typeof entry === "object" && entry !== null && typeof entry.id === "string" && !KNOWN_CHECK_IDS.has(entry.id))
  ) : raw,
  z6.array(DeclarationSchema).max(CHECK_IDS.length).superRefine((entries, ctx) => {
    const seen = /* @__PURE__ */ new Set();
    for (const entry of entries) {
      if (seen.has(entry.id)) {
        ctx.addIssue({
          code: z6.ZodIssueCode.custom,
          message: `check ${entry.id} is declared more than once`
        });
      }
      seen.add(entry.id);
      if (entry.status === "na" && (entry.summary ?? "").trim() === "") {
        ctx.addIssue({
          code: z6.ZodIssueCode.custom,
          message: `check ${entry.id} declares na without a reason`
        });
      }
    }
  })
);
var ReadinessFileSchema = z6.object({
  schemaVersion: z6.literal(1),
  coverage: z6.object({
    path: RepoRelativePathSchema.optional(),
    threshold: z6.number().finite().min(0).max(100).optional()
  }).strict().optional(),
  productionPaths: z6.object({
    include: z6.array(RepoRelativePathSchema).max(MAX_PATH_PATTERNS).optional(),
    ignore: z6.array(RepoRelativePathSchema).max(MAX_PATH_PATTERNS).optional()
  }).strict().optional(),
  checks: DeclarationsSchema.optional()
}).strict();

// src/schemas/read.ts
import { z as z7 } from "zod";
var ReadResponseSchema = z7.object({
  content: z7.string(),
  mtime: z7.string().datetime(),
  sha256: z7.string().regex(/^[a-f0-9]{64}$/)
});
var ALLOWED_SUBDIRS = [".planning", ".claude", "openspec"];
function isReadableProjectPath(path) {
  if (path === "") return false;
  if (path.startsWith("/") || path.startsWith("~")) return false;
  if (/^[A-Za-z]:/.test(path)) return false;
  const segments = path.split(/[/\\]/);
  if (segments.some((segment) => segment === "" || segment === "." || segment === "..")) {
    return false;
  }
  return segments.length > 1 && ALLOWED_SUBDIRS.some((dir) => segments[0] === dir);
}

// src/schemas/git.ts
import { z as z8 } from "zod";
var GitResponseSchema = z8.object({
  stdout: z8.string(),
  stderr: z8.string(),
  exitCode: z8.number().int()
});

// src/schemas/errors.ts
import { z as z9 } from "zod";
var ErrorResponseSchema = z9.object({
  ok: z9.literal(false),
  error: z9.string().min(1),
  requestId: z9.string().min(1),
  issues: z9.array(
    z9.object({
      path: z9.array(z9.string()),
      message: z9.string()
    })
  ).optional()
});

// src/schemas/server.ts
import { z as z10 } from "zod";
var ServerInfoSchema = z10.object({
  bindUrl: z10.string().url(),
  pid: z10.number().int().positive(),
  startedAt: z10.string().datetime()
});

// src/schemas/pairing.ts
import { z as z11 } from "zod";
var AGENT_URL_REGEX = /^(?:http:\/\/(?:localhost|127\.0\.0\.1)(?::\d{1,5})?|https?:\/\/[a-z0-9-]+(?:\.[a-z0-9-]+)+\.ts\.net(?::\d{1,5})?)$/i;
var AgentUrlSchema = z11.string().regex(AGENT_URL_REGEX, {
  message: "agent URL must be loopback or *.ts.net"
});
var PairingSchema = z11.object({
  agentUrl: AgentUrlSchema,
  token: TokenSchema,
  pairedAt: z11.string().datetime()
});

// src/schemas/commitment.ts
import { z as z12 } from "zod";
var CommitmentBlockResponseSchema = z12.object({
  markdown: z12.string().nullable(),
  sourceFile: z12.string().nullable()
});

// src/schemas/observations.ts
import { z as z13 } from "zod";
var HookFiringSchema = z13.object({
  ts: z13.string(),
  skill: z13.string(),
  hook: z13.string()
}).passthrough();
var ObservationsRecentResponseSchema = z13.object({
  entries: z13.array(HookFiringSchema),
  skillInstalled: z13.boolean()
});

// src/schemas/discipline.ts
import { z as z14 } from "zod";
var RationalizationRowSchema = z14.object({
  label: z14.string(),
  fires: z14.number().int().nonnegative()
});
var DisciplineResponseSchema = z14.object({
  rationalization: z14.object({
    rows: z14.array(RationalizationRowSchema),
    skillInstalled: z14.boolean()
  })
});

// src/schemas/skills.ts
import { z as z15 } from "zod";
var SkillFrontmatterSchema = z15.object({
  name: z15.string(),
  description: z15.string().optional(),
  version: z15.string().optional()
}).passthrough();
var SkillEntrySchema = SkillFrontmatterSchema.extend({
  dir: z15.string(),
  scope: z15.enum(["global", "local"])
});
var GlobalSkillsResponseSchema = z15.object({
  scope: z15.literal("global"),
  skills: z15.array(SkillEntrySchema)
});
var LocalSkillsResponseSchema = z15.object({
  scope: z15.literal("local"),
  skills: z15.array(SkillEntrySchema)
});

// src/schemas/pathDrift.ts
import { z as z16 } from "zod";
var PathDriftReasonSchema = z16.enum([
  "missing",
  "symlink-target-changed",
  "git-remote-changed"
]);
var PathDriftEntrySchema = z16.object({
  id: z16.string(),
  // registry entry id
  storedPath: z16.string(),
  // value currently in registry.json
  suggestedPath: z16.string().nullable(),
  // null when inference failed
  reason: PathDriftReasonSchema
}).strict();
var RegistryFixPathRequestSchema = z16.object({
  id: z16.string().min(1),
  newPath: z16.string().min(1)
}).strict();

// src/schemas/workflow.ts
import { z as z17 } from "zod";
var SymbolicIdSchema = z17.string().regex(/^[A-Za-z0-9][A-Za-z0-9._-]*$/);
var SemverSchema = z17.string().regex(
  /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-[0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*)?(?:\+[0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*)?$/
);
var Sha256Schema = z17.string().regex(/^[0-9a-f]{64}$/);
var CommitSchema = z17.string().regex(/^[0-9a-fA-F]{40}$/);
var WorkflowHostIdSchema = z17.enum([
  "claude-workflow",
  "codex-workflow",
  "opencode-workflow",
  "pi-agentic-apps-workflow"
]);
var WorkflowHarnessIdSchema = z17.enum(["change-gate", "reviewer-cli"]);
var WorkflowSkillSchema = z17.object({
  id: SymbolicIdSchema,
  name: SymbolicIdSchema,
  state: z17.enum(["known", "unknown", "missing"]),
  version: SemverSchema.nullable(),
  reason: z17.enum([
    "expected-skill-missing",
    "frontmatter-missing",
    "implements-spec-missing",
    "implements-spec-duplicate",
    "implements-spec-malformed"
  ]).optional()
}).strict();
var WorkflowArtifactIdSchema = z17.enum([
  "change-gate",
  "reviewer-cli",
  "change-gate-harness",
  "reviewer-cli-harness"
]);
var WorkflowArtifactSchema = z17.object({
  artifactId: WorkflowArtifactIdSchema,
  state: z17.enum([
    "identical",
    "divergent",
    "missing",
    "pinned",
    "unavailable"
  ]),
  sha256: Sha256Schema.nullable(),
  referenceSha256: Sha256Schema.nullable(),
  marker: z17.object({
    state: z17.enum(["valid", "absent", "invalid", "not-applicable"]),
    version: SemverSchema.nullable()
  }).strict(),
  provenance: z17.object({
    state: z17.enum(["valid", "absent", "invalid"]),
    commit: CommitSchema.nullable()
  }).strict(),
  // How a host distributes an artefact it does not vendor. `behind` means the
  // recorded digest differs from core's CURRENT reference — the host is
  // pinned to something else — and deliberately not that the manifest lies,
  // which cannot be established without reading core at the pinned commit.
  pin: z17.object({
    state: z17.enum([
      "intact",
      "behind",
      "unlisted",
      "invalid",
      "not-declared"
    ]),
    commit: CommitSchema.nullable(),
    recordedSha256: Sha256Schema.nullable()
  }).strict()
}).strict();
function workflowHostSchema(hostId) {
  return z17.object({
    hostId: z17.literal(hostId),
    state: z17.enum(["available", "missing"]),
    primary: WorkflowSkillSchema.nullable(),
    skills: z17.array(WorkflowSkillSchema),
    minimum: SemverSchema.nullable(),
    maximum: SemverSchema.nullable(),
    laggards: z17.array(
      z17.object({
        id: SymbolicIdSchema,
        name: SymbolicIdSchema,
        version: SemverSchema
      }).strict()
    ),
    unknowns: z17.array(WorkflowSkillSchema),
    internallyConsistent: z17.boolean(),
    coreState: z17.enum(["current", "behind", "ahead", "unavailable"]),
    divergent: z17.boolean(),
    artifacts: z17.array(WorkflowArtifactSchema),
    migration: z17.object({
      kind: z17.literal("offered"),
      highest: z17.string().regex(/^\d{4}$/).nullable()
    }).strict()
  }).strict();
}
function machineRootSchema(rootId) {
  return z17.object({
    rootId: z17.literal(rootId),
    state: z17.enum(["present", "absent"]),
    entries: z17.array(
      z17.object({
        id: SymbolicIdSchema,
        state: z17.enum(["present", "missing"]),
        artifact: WorkflowArtifactSchema.optional()
      }).strict()
    )
  }).strict();
}
var WorkflowResponseSchema = z17.object({
  schemaVersion: z17.literal(1),
  generatedAtIso: z17.string().datetime(),
  core: z17.object({
    repoId: z17.literal("agenticapps-workflow-core"),
    state: z17.enum(["available", "missing"]),
    specVersion: SemverSchema.nullable()
  }).strict(),
  hosts: z17.tuple([
    workflowHostSchema("claude-workflow"),
    workflowHostSchema("codex-workflow"),
    workflowHostSchema("opencode-workflow"),
    workflowHostSchema("pi-agentic-apps-workflow")
  ]),
  machineRoots: z17.tuple([
    machineRootSchema("agenticapps-bin"),
    machineRootSchema("claude-skills"),
    machineRootSchema("codex-skills"),
    machineRootSchema("opencode-skills"),
    machineRootSchema("pi-skills")
  ])
}).strict();
var WorkflowHarnessRequestSchema = z17.object({
  hostId: WorkflowHostIdSchema,
  harnessId: WorkflowHarnessIdSchema
}).strict();
var HarnessResultBaseSchema = z17.object({
  schemaVersion: z17.literal(1),
  hostId: WorkflowHostIdSchema,
  harnessId: WorkflowHarnessIdSchema,
  output: z17.string().max(1024 * 1024),
  cached: z17.boolean()
});
var CompletedHarnessResultSchema = HarnessResultBaseSchema.extend({
  state: z17.literal("completed"),
  passed: z17.boolean(),
  completedAtIso: z17.string().datetime(),
  ageMs: z17.number().int().nonnegative(),
  cached: z17.boolean()
}).strict();
function incompleteHarnessResultSchema(state, reasons) {
  return HarnessResultBaseSchema.extend({
    state: z17.literal(state),
    passed: z17.null(),
    completedAtIso: z17.null(),
    ageMs: z17.null(),
    cached: z17.literal(false),
    reason: z17.enum(reasons)
  }).strict();
}
var WorkflowHarnessResultSchema = z17.discriminatedUnion("state", [
  CompletedHarnessResultSchema,
  incompleteHarnessResultSchema("refused", [
    "unknown-selection",
    "repo-unavailable",
    "path-not-allowed",
    "harness-missing",
    "harness-not-executable",
    "harness-divergent",
    "spawn-failed"
  ]),
  incompleteHarnessResultSchema("busy", ["host-busy", "capacity-busy"]),
  incompleteHarnessResultSchema("timeout", ["time-limit"]),
  incompleteHarnessResultSchema("bounded-out", [
    "memory-limit",
    "output-limit",
    "scratch-limit"
  ])
]);

// src/clipboard.ts
var UNDERSTAND_TARGET_SEGMENT_RE = /^[a-z0-9][a-z0-9\-_.]*$/;
function buildUnderstandCommand(family, repo) {
  if (!UNDERSTAND_TARGET_SEGMENT_RE.test(family) || !UNDERSTAND_TARGET_SEGMENT_RE.test(repo)) {
    throw new TypeError(
      `buildUnderstandCommand: family/repo must match ${String(UNDERSTAND_TARGET_SEGMENT_RE)}; got family=${JSON.stringify(family)} repo=${JSON.stringify(repo)}`
    );
  }
  return {
    string: `cd ~/Sourcecode/${family}/${repo} && claude "/understand"`,
    argv: ["/understand"]
  };
}

// src/schemas/changes.ts
import { z as z18 } from "zod";
var CHANGE_STAGES = ["propose", "validate", "execute", "archive"];
var ChangeStageSchema = z18.enum(CHANGE_STAGES);
var CHANGE_SOURCES = ["active", "archive", "backlog"];
var ChangeSourceSchema = z18.enum(CHANGE_SOURCES);
function cardKey(repositoryId, source, sourceInstance) {
  return `${repositoryId}\0${source}\0${sourceInstance}`;
}
var ChangeArtifactsSchema = z18.object({
  proposal: z18.enum(["ready", "missing"]),
  /** How many `specs/<name>/spec.md` files under the change read successfully. */
  deltaSpecCount: z18.number().int().nonnegative(),
  tasks: z18.enum(["ready", "missing"]),
  // Never "missing": `design.md` is optional, and its absence is not a defect
  // — nor does it affect any stage.
  design: z18.enum(["ready", "optional"])
}).strict();
var ChangeChecklistRowSchema = z18.object({ text: z18.string(), completed: z18.boolean() }).strict();
var ChangeCardSchema = z18.object({
  /** `cardKey(repositoryId, source, sourceInstance)`. Unique by construction. */
  id: z18.string().min(1),
  repositoryId: z18.string().min(1),
  repositoryName: z18.string().min(1),
  source: ChangeSourceSchema,
  /**
   * The entry's own on-disk name: an active change's directory name, an
   * archived entry's full dated `YYYY-MM-DD-<slug>` basename, or a backlog
   * entry's `backlogSlug(title)`.
   */
  sourceInstance: z18.string().min(1),
  /** The slug. Equal to `sourceInstance` except for a dated archive entry. */
  changeName: z18.string().min(1),
  /** What to display. The raw heading text for a backlog entry, else the slug. */
  title: z18.string().min(1),
  stage: ChangeStageSchema,
  /**
   * Rule 5: an active change complete enough to archive. The marker is what
   * makes the two archive readings legible without opening the card — a
   * filed archive carries `source: 'archive'`, this carries
   * `source: 'active'` and `ready: true`.
   */
  ready: z18.boolean(),
  artifacts: ChangeArtifactsSchema,
  reviewerVendors: z18.array(z18.string()),
  hasRequestChanges: z18.boolean(),
  /**
   * Which review record the stage was classified from — `REVIEWS.md` or a
   * `REVIEWS-round-N.md` — or null when the change carries none. The card
   * names the record rather than merely noting that several exist.
   */
  reviewRecord: z18.string().nullable(),
  checklist: z18.array(ChangeChecklistRowSchema),
  completedChecklist: z18.number().int().nonnegative(),
  totalChecklist: z18.number().int().nonnegative(),
  /** `YYYY-MM-DD` for a dated archive entry, null otherwise. Orders the Archive column. */
  archiveDate: z18.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
  /** Newest evidence modification time, epoch ms. Orders the other three columns. */
  updatedAt: z18.number().int().nonnegative(),
  /** The read hit a bound, so this card's evidence is partial. */
  evidenceLimited: z18.boolean()
}).strict();
var CHANGE_NOTICE_KINDS = [
  "collision",
  "empty-slug",
  "evidence-limited",
  "malformed",
  "rejected",
  "truncated"
];
var ChangeNoticeKindSchema = z18.enum(CHANGE_NOTICE_KINDS);
var ChangeNoticeSchema = z18.object({
  repositoryId: z18.string().min(1),
  repositoryName: z18.string().min(1),
  // `evidence` is a notice source but never a card source: it reports a
  // bounded read of a change's artifacts, not a place a card came from.
  source: z18.enum([...CHANGE_SOURCES, "evidence"]),
  kind: ChangeNoticeKindSchema,
  admitted: z18.number().int().nonnegative().optional(),
  observed: z18.number().int().nonnegative().optional(),
  /** The slug the notice concerns, where it concerns one. Never a path. */
  changeName: z18.string().optional()
}).strict();
var CHANGE_READ_FAILURES = ["unreachable", "timeout", "unreadable"];
var ChangeReadFailureSchema = z18.enum(CHANGE_READ_FAILURES);
var ChangeRepositoryStatusSchema = z18.object({
  id: z18.string().min(1),
  name: z18.string().min(1),
  read: z18.enum(["ok", "failed"]),
  reason: ChangeReadFailureSchema.nullable()
}).strict();
var ChangesFleetResponseSchema = z18.object({
  generatedAt: z18.number().int().nonnegative(),
  cards: z18.array(ChangeCardSchema),
  /** Every registered repository, read or not. Absence from this list is a bug. */
  repositories: z18.array(ChangeRepositoryStatusSchema),
  notices: z18.array(ChangeNoticeSchema)
}).strict();
function compareChangeCards(left, right) {
  if (left.stage !== right.stage) {
    return CHANGE_STAGES.indexOf(left.stage) - CHANGE_STAGES.indexOf(right.stage);
  }
  if (left.stage === "archive") {
    if (left.archiveDate === null && right.archiveDate !== null) return -1;
    if (left.archiveDate !== null && right.archiveDate === null) return 1;
    if (left.archiveDate !== null && right.archiveDate !== null && left.archiveDate !== right.archiveDate) {
      return left.archiveDate < right.archiveDate ? 1 : -1;
    }
  } else if (left.updatedAt !== right.updatedAt) {
    return right.updatedAt - left.updatedAt;
  }
  return left.id < right.id ? -1 : left.id > right.id ? 1 : 0;
}
function fleetReadState(response) {
  const failed = response.repositories.filter((repo) => repo.read === "failed");
  if (failed.length > 0) {
    return failed.length === response.repositories.length ? "all-failed" : "degraded";
  }
  return response.cards.length > 0 ? "ok" : "empty";
}
export {
  ADVISORY_WHEN_UNDECLARED,
  AGENT_URL_REGEX,
  ALLOWED_SUBDIRS,
  AgentUrlSchema,
  AuthFileSchema,
  CHANGE_NOTICE_KINDS,
  CHANGE_READ_FAILURES,
  CHANGE_SOURCES,
  CHANGE_STAGES,
  CHECK_IDS,
  CHECK_STATUSES,
  ChangeArtifactsSchema,
  ChangeCardSchema,
  ChangeChecklistRowSchema,
  ChangeNoticeKindSchema,
  ChangeNoticeSchema,
  ChangeReadFailureSchema,
  ChangeRepositoryStatusSchema,
  ChangeSourceSchema,
  ChangeStageSchema,
  ChangesFleetResponseSchema,
  CheckIdSchema,
  CheckResultSchema,
  CheckSourceSchema,
  CheckStatusSchema,
  CommitmentBlockResponseSchema,
  DisciplineResponseSchema,
  ErrorResponseSchema,
  FleetResponseSchema,
  GitResponseSchema,
  GlobalSkillsResponseSchema,
  HealthResponseSchema,
  HookFiringSchema,
  LocalSkillsResponseSchema,
  MarkersSchema,
  ObservationsRecentResponseSchema,
  OpenChangeSummarySchema,
  OpenspecCapabilitySchema,
  OpenspecChangeDetailSchema,
  OpenspecProjectStateSchema,
  PairingSchema,
  PathDriftEntrySchema,
  PathDriftReasonSchema,
  ProjectConditionSchema,
  ProjectOverviewSchema,
  RationalizationRowSchema,
  ReadResponseSchema,
  ReadinessFileSchema,
  ReadinessNoticeSchema,
  RegisterConfirmRequestSchema,
  RegisterConfirmResponseSchema,
  RegisterPrepareRequestSchema,
  RegisterPrepareResponseSchema,
  RegisterResponseSchema,
  RegistryEntrySchema,
  RegistryFileSchema,
  RegistryFixPathRequestSchema,
  RegistryListItemSchema,
  RegistryListResponseSchema,
  RenameRequestSchema,
  RepoDetailResponseSchema,
  RepoDetailSchema,
  RepoFamilySchema,
  RepoRelativePathSchema,
  RepoSummarySchema,
  ServerInfoSchema,
  SkillEntrySchema,
  SkillFrontmatterSchema,
  StatusResponseSchema,
  TagsRequestSchema,
  TokenSchema,
  WorkflowHarnessRequestSchema,
  WorkflowHarnessResultSchema,
  WorkflowResponseSchema,
  buildUnderstandCommand,
  cardKey,
  carriesAbsolutePath,
  compareChangeCards,
  computeReady,
  fleetReadState,
  isAdvisoryCheck,
  isReadableProjectPath,
  wireSafeText
};
